import { useState, useEffect, useRef, useCallback } from 'react';
import { X, ZoomIn, ZoomOut, AlertCircle, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, IconButton, Skeleton } from './ui';
import type { TransactionLocation } from '../types';

/** Header icon controls sit on dark green-900 chrome, so the ghost variant's
 *  light-surface colors are overridden here (presentation only). */
const CHROME_ICON_BTN =
  'text-green-200 hover:text-white hover:bg-green-800 disabled:bg-transparent disabled:border-transparent disabled:text-chrome-muted';

interface SourcePageViewerProps {
  statementId: number;
  pageNumber: number;
  sourceFile: string;
  isOpen: boolean;
  onClose: () => void;
  highlight?: TransactionLocation | null;
}

interface PageProps {
  statementId: number;
  pageNumber: number;
  sourceFile: string;
  zoomed: boolean;
  isTarget: boolean;
  highlight?: TransactionLocation | null;
  registerRef: (pageNumber: number, el: HTMLDivElement | null) => void;
}

function StatementPage({
  statementId,
  pageNumber,
  sourceFile,
  zoomed,
  isTarget,
  highlight,
  registerRef,
}: PageProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const urlRef = useRef<string | null>(null);
  const fetchedRef = useRef(false);

  // Lazy-load: only fetch the page image once it scrolls near the viewport.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { root: el.closest('[data-pages-scroll]'), rootMargin: '600px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || fetchedRef.current) return;
    fetchedRef.current = true;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetch(`/api/statements/${statementId}/page/${pageNumber}`, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load page (${res.status})`);
        return res.blob();
      })
      .then((blob) => {
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        setImageUrl(url);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        fetchedRef.current = false; // allow a retry on next visibility/render
        setError(err.message || 'Failed to load page');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [visible, statementId, pageNumber]);

  useEffect(() => {
    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, []);

  return (
    <div
      ref={(el) => {
        containerRef.current = el;
        registerRef(pageNumber, el);
      }}
      className="flex flex-col items-center"
    >
      <div className="text-xs text-ink-faint mb-1">Page {pageNumber}</div>
      <div className="relative min-h-[120px] w-full flex justify-center items-start">
        {!imageUrl && loading && (
          <Skeleton variant="rect" className="aspect-[85/110] w-full max-w-lg rounded-xs" />
        )}
        {!imageUrl && error && (
          <div className="flex flex-col items-center justify-center py-16">
            <AlertCircle size={20} className="text-danger" />
            <p className="mt-2 text-xs text-danger">{error}</p>
          </div>
        )}
        {imageUrl && (
          <div className="relative inline-block">
            <img
              src={imageUrl}
              alt={`${sourceFile} - Page ${pageNumber}`}
              className={`block rounded-xs border ${
                isTarget ? 'border-green-600 ring-2 ring-green-300' : 'border-border'
              } ${zoomed ? 'max-w-none w-full' : 'max-w-full max-h-[75vh]'}`}
            />
            {isTarget && highlight && (
              <div
                className="absolute pointer-events-none rounded-xs"
                style={{
                  left: `${highlight.x * 100}%`,
                  top: `${highlight.y * 100}%`,
                  width: `${highlight.w * 100}%`,
                  height: `${highlight.h * 100}%`,
                  backgroundColor: 'color-mix(in srgb, var(--color-green-700) 22%, transparent)',
                  border: '2px solid var(--color-green-700)',
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function SourcePageViewer({
  statementId,
  pageNumber,
  sourceFile,
  isOpen,
  onClose,
  highlight,
}: SourcePageViewerProps) {
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const [countError, setCountError] = useState<string | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const [currentPage, setCurrentPage] = useState(pageNumber);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const didScrollToTarget = useRef(false);

  const registerRef = useCallback((pn: number, el: HTMLDivElement | null) => {
    if (el) pageRefs.current.set(pn, el);
    else pageRefs.current.delete(pn);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setPageCount(null);
      setCountError(null);
      setZoomed(false);
      didScrollToTarget.current = false;
      pageRefs.current.clear();
      return;
    }

    setCurrentPage(pageNumber);
    didScrollToTarget.current = false;
    const controller = new AbortController();
    setCountLoading(true);
    setCountError(null);

    fetch(`/api/statements/${statementId}/page-count`, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load statement (${res.status})`);
        return res.json();
      })
      .then((data) => {
        const n = typeof data.page_count === 'number' && data.page_count > 0 ? data.page_count : 1;
        setPageCount(Math.max(n, pageNumber));
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        // Fall back to at least the requested page so the user still sees it.
        setPageCount(Math.max(1, pageNumber));
        setCountError(err.message || 'Could not load the full statement; showing available pages.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setCountLoading(false);
      });

    return () => controller.abort();
  }, [isOpen, statementId, pageNumber]);

  // Once the page list is rendered, jump to the transaction's source page.
  useEffect(() => {
    if (!isOpen || pageCount === null || didScrollToTarget.current) return;
    const t = setTimeout(() => {
      const target = pageRefs.current.get(pageNumber);
      if (target) {
        target.scrollIntoView({ behavior: 'auto', block: 'start' });
        didScrollToTarget.current = true;
      }
    }, 120);
    return () => clearTimeout(t);
  }, [isOpen, pageCount, pageNumber]);

  // Track which page is currently centered for the "Page X of N" indicator.
  const handleScroll = useCallback(() => {
    const scroller = scrollRef.current;
    if (!scroller || pageCount === null) return;
    const mid = scroller.scrollTop + scroller.clientHeight / 2;
    let best = currentPage;
    let bestDist = Infinity;
    pageRefs.current.forEach((el, pn) => {
      const center = el.offsetTop + el.offsetHeight / 2;
      const dist = Math.abs(center - mid);
      if (dist < bestDist) {
        bestDist = dist;
        best = pn;
      }
    });
    if (best !== currentPage) setCurrentPage(best);
  }, [pageCount, currentPage]);

  const goToPage = useCallback((pn: number) => {
    if (pageCount === null) return;
    const clamped = Math.min(Math.max(pn, 1), pageCount);
    const target = pageRefs.current.get(clamped);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setCurrentPage(clamped);
    }
  }, [pageCount]);

  if (!isOpen) return null;

  const pages = pageCount ? Array.from({ length: pageCount }, (_, i) => i + 1) : [];

  return (
    <div className="fixed inset-0 z-(--z-modal) flex items-center justify-center">
      <div className="absolute inset-0 bg-ink/40 animate-scrim" onClick={onClose} />

      <div className="relative bg-cream-light rounded-overlay shadow-modal animate-dialog max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        <div
          data-surface="chrome"
          className="flex items-center justify-between px-5 py-3 bg-green-900 rounded-t-overlay"
        >
          <div className="flex items-center gap-3">
            <FileText size={16} className="text-green-200" aria-hidden="true" />
            <div>
              <h3 className="t-label text-chrome-text-strong">{sourceFile}</h3>
              <p className="text-green-200 text-xs">
                {pageCount ? `Page ${currentPage} of ${pageCount}` : 'Loading…'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pageCount && pageCount > 1 && (
              <div className="flex items-center gap-1 mr-1">
                <IconButton
                  icon={ChevronLeft}
                  size="sm"
                  variant="ghost"
                  aria-label="Previous page"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className={CHROME_ICON_BTN}
                />
                <IconButton
                  icon={ChevronRight}
                  size="sm"
                  variant="ghost"
                  aria-label="Next page"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= pageCount}
                  className={CHROME_ICON_BTN}
                />
              </div>
            )}
            {pageCount && (
              <IconButton
                icon={zoomed ? ZoomOut : ZoomIn}
                size="sm"
                variant="ghost"
                aria-label={zoomed ? 'Fit to width' : 'Zoom in'}
                onClick={() => setZoomed(!zoomed)}
                className={CHROME_ICON_BTN}
              />
            )}
            <IconButton
              icon={X}
              size="sm"
              variant="ghost"
              aria-label="Close"
              onClick={onClose}
              className={CHROME_ICON_BTN}
            />
          </div>
        </div>

        <div
          ref={scrollRef}
          data-pages-scroll
          onScroll={handleScroll}
          className="flex-1 overflow-auto p-4 bg-cream-dark space-y-6"
        >
          {countLoading && pageCount === null && (
            <div role="status" aria-label="Loading statement" className="flex flex-col items-center py-8">
              <Skeleton variant="rect" className="aspect-[85/110] w-full max-w-lg rounded-xs" />
              <span className="sr-only">Loading statement</span>
            </div>
          )}

          {countError && pageCount === null && (
            <div className="flex flex-col items-center justify-center py-20">
              <AlertCircle size={24} className="text-danger" />
              <p className="mt-3 text-sm text-danger">{countError}</p>
              <Button variant="secondary" size="sm" onClick={onClose} className="mt-4">
                Close
              </Button>
            </div>
          )}

          {countError && pageCount !== null && (
            <div className="flex items-center gap-2 rounded-md border border-warn bg-warn-soft px-3 py-2 text-xs text-warn">
              <AlertCircle size={12} className="shrink-0" />
              {countError}
            </div>
          )}

          {pages.map((pn) => (
            <StatementPage
              key={pn}
              statementId={statementId}
              pageNumber={pn}
              sourceFile={sourceFile}
              zoomed={zoomed}
              isTarget={pn === pageNumber}
              highlight={highlight}
              registerRef={registerRef}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
